/** True when the SKU code is the standard RTR product or a company-specific RTR_* code. */
export function isBillableRtrSkuCode(code: string | null | undefined): boolean {
  const upper = (code ?? '').trim().toUpperCase();
  return upper === 'RTR' || upper.startsWith('RTR_');
}

/** Resolve the standard portal RTR product (sku code "RTR" / RealTime Research). */
export function resolveGenericRtrSku(
  skus: Array<{ sku?: string | null; name?: string | null }>,
): string | null {
  const exact = skus.find((s) => (s.sku ?? '').trim().toUpperCase() === 'RTR');
  if (exact) {
    return exact.sku ?? 'RTR';
  }
  const byName = skus.find((s) => /realtime\s*research/i.test(s.name ?? ''));
  return byName?.sku ?? byName?.name ?? null;
}

/** True when the row looks like a billable RTR product (not DNB_RTR_*). */
export function isBillableRtrRow(sku: { sku?: string | null; name?: string | null }): boolean {
  const code = (sku.sku ?? sku.name ?? '').trim().toUpperCase();
  if (!code || !code.includes('RTR')) {
    return false;
  }
  return !code.startsWith('DNB');
}

function normaliseForMatch(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function titleTokens(title: string): string[] {
  const cleaned = title.replace(/\bRTR\b/gi, '');
  const tokens = cleaned.match(/[\w]{3,}/g) ?? [];
  const stop = new Set(['the', 'and', 'for', 'from', 'with']);
  return tokens.map((t) => t.toLowerCase()).filter((t) => !stop.has(t));
}

function scoreRtrTitleMatch(
  title: string,
  sku: { sku?: string | null; name?: string | null },
): number {
  const tokens = titleTokens(title);
  if (tokens.length === 0) {
    return 0;
  }

  const hay = `${sku.sku ?? ''} ${sku.name ?? ''}`.toLowerCase();
  let score = tokens.filter((token) => hay.includes(token)).length;

  const titleNorm = normaliseForMatch(title.replace(/\bRTR\b/gi, ''));
  for (const field of [sku.sku, sku.name]) {
    const fieldNorm = normaliseForMatch(field ?? '');
    if (titleNorm && fieldNorm && (titleNorm.includes(fieldNorm) || fieldNorm.includes(titleNorm))) {
      return 1000 + score;
    }
  }
  return score;
}

/** Match an RTR SKU from a campaign / line-item title (e.g. "United St. Louis Outlook RTR"). */
export function resolveRtrSkuFromTitle(
  title: string | null | undefined,
  skus: Array<{ sku?: string | null; name?: string | null }>,
): string | null {
  if (!title?.trim() || !/\bRTR\b/i.test(title)) {
    return null;
  }

  const candidates = skus.filter(isBillableRtrRow);
  const scored = candidates
    .map((sku) => ({ sku, score: scoreRtrTitleMatch(title, sku) }))
    .filter((entry) => entry.score > 0);

  if (scored.length === 0) {
    return null;
  }

  scored.sort((a, b) => b.score - a.score);
  const best = scored[0];
  const tokenCount = titleTokens(title).length;
  const minScore = tokenCount <= 2 ? 1 : 2;
  if (best.score >= 1000 || best.score >= minScore) {
    return best.sku.sku ?? best.sku.name ?? null;
  }
  return null;
}

/** Find the company-specific RTR SKU code from the synced product catalogue. */
export function resolveCompanyRtrSku(
  companyName: string | null | undefined,
  skus: Array<{ sku?: string | null; name?: string | null }>,
): string | null {
  const normalisedCompany = companyName?.trim().toLowerCase();
  if (!normalisedCompany) {
    return null;
  }

  const rtrSkus = skus.filter(isBillableRtrRow);
  const matches = rtrSkus.filter((sku) => {
    const code = (sku.sku ?? sku.name ?? '').toLowerCase();
    const name = (sku.name ?? '').toLowerCase();
    return code.includes(normalisedCompany) || name.includes(normalisedCompany);
  });

  if (matches.length === 0) {
    return null;
  }
  if (matches.length === 1) {
    return matches[0].sku ?? matches[0].name ?? null;
  }

  matches.sort((a, b) => (b.sku?.length ?? b.name?.length ?? 0) - (a.sku?.length ?? a.name?.length ?? 0));
  return matches[0].sku ?? matches[0].name ?? null;
}

function effectiveSkuCode(sku: { sku?: string | null; name?: string | null }): string {
  return (sku.sku?.trim() || sku.name?.trim() || '').toUpperCase();
}

/** Display label for SKU dropdowns (code + name when both differ). */
export function formatSkuLabel(sku: { sku?: string | null; name?: string | null }): string {
  const code = sku.sku?.trim();
  const name = sku.name?.trim();
  if (code && name && code.toUpperCase() !== name.toUpperCase()) {
    return `${code} — ${name}`;
  }
  return code || name || '';
}

/** SKUs with null status are treated as selectable (legacy rows). */
export function isSelectableSku(sku: { status?: string | null }): boolean {
  return (sku.status ?? 'active').toLowerCase() !== 'archived';
}

export function skuMatchesCode(
  sku: { sku?: string | null; name?: string | null },
  code: string,
): boolean {
  const normalised = code.toUpperCase().trim();
  if (!normalised) {
    return false;
  }

  const skuCode = effectiveSkuCode(sku);
  if (skuCode === normalised) {
    return true;
  }

  const skuName = sku.name?.toUpperCase().trim();
  if (skuName === normalised) {
    return true;
  }

  if (skuName?.startsWith(`${normalised} `) || skuName?.startsWith(`${normalised}-`)) {
    return true;
  }

  // RTR codes are long and may differ slightly between suggestion and catalogue row.
  if (normalised.includes('RTR') || isBillableRtrSkuCode(sku.sku) || isBillableRtrSkuCode(sku.name)) {
    const suggestionCore = normalised.replace(/[^A-Z0-9]+/g, '');
    const codeCore = skuCode.replace(/[^A-Z0-9]+/g, '');
    const nameCore = (skuName ?? '').replace(/[^A-Z0-9]+/g, '');
    if (
      (codeCore && (suggestionCore.includes(codeCore) || codeCore.includes(suggestionCore)))
      || (nameCore && (suggestionCore.includes(nameCore) || nameCore.includes(suggestionCore)))
    ) {
      return true;
    }
  }

  return false;
}

export function resolveSkuIdFromSuggestion(
  suggestionCode: string | null,
  skus: Array<{ id: string; sku?: string | null; name?: string | null }>,
): string | null {
  if (suggestionCode === null) {
    return null;
  }
  const match = skus.find((sku) => skuMatchesCode(sku, suggestionCode));
  return match?.id ?? null;
}
